<template>
  <div class="approvals-panel w-100">
    <FlsaPanelSkeleton v-if="isLoading" />

    <div
      v-else
      class="pb-6"
    >
      <HideRbac :permissions="['createJobApprovalFlow']">
        <ApprovalsCreateTemplate
          v-if="createStep === 'template' && !hasActiveApprovalFlow"
        />
        <ApprovalsAssignApprovers
          v-else-if="createStep === 'assign'"
          @submit="handleSubmitApprovalFlow"
        />
      </HideRbac>

      <HideRbac
        v-if="createStep === 'template'"
        :permissions="['editJobApprovalFlow']"
      >
        <ApprovalsList
          :flows="flows"
          :templates="templates"
          :in-approval-process="hasActiveApprovalFlow"
          :job-profile-title="jobProfile?.title"
          @flow-click="handleFlowClick"
        />
      </HideRbac>

      <HideRbac
        v-if="createStep === 'detail'"
        :permissions="['editJobApprovalFlow']"
      >
        <ApprovalsFlowDetail :initial-show-started-banner="initialShowStartedBanner" />
      </HideRbac>

      <HideRbac
        v-if="createStep === 'edit'"
        :permissions="['editJobApprovalFlow']"
      >
        <ApprovalsFlowEdit />
      </HideRbac>
    </div>
  </div>
</template>

<script setup>
import {
  onMounted,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import ApprovalsAssignApprovers from '@/components/jobProfiles/editor/ApprovalsAssignApprovers.vue';
import ApprovalsCreateTemplate from '@/components/jobProfiles/editor/ApprovalsCreateTemplate.vue';
import ApprovalsFlowDetail from '@/components/jobProfiles/editor/approvals/ApprovalsFlowDetail.vue';
import ApprovalsFlowEdit from '@/components/jobProfiles/editor/approvals/ApprovalsFlowEdit.vue';
import ApprovalsList from '@/components/jobProfiles/editor/ApprovalsList.vue';
import FlsaPanelSkeleton from '@/components/jobProfiles/editor/FlsaPanelSkeleton.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useApprovalFlows } from '@/stores/jobProfiles/approvalFlows';
import { useAppSnackbar } from '@/stores/app/snackbar';

const route = useRoute();
const router = useRouter();
const { addErrorSnackbar } = useAppSnackbar();

const { jobProfile, isHrisProfile } = storeToRefs(useCurrentJobProfile());

const approvalFlowsStore = useApprovalFlows();
const {
  templates,
  flows,
  selectedTemplateUlid,
  createStep,
  isLoading,
  initialShowStartedBanner,
  hasActiveApprovalFlow,
} = storeToRefs(approvalFlowsStore);
const {
  loadPanelData,
  createFlow,
  openFlowDetail,
  openFlowForEdit,
  viewOtherApprovals,
} = approvalFlowsStore;

const applyApprovalsDeepLink = () => {
  const { approvalFlow, approvalStep, ...restQuery } = route.query;

  if (!approvalFlow || approvalStep !== 'edit') return;

  const opened = openFlowForEdit(approvalFlow);

  if (!opened) {
    addErrorSnackbar({ text: 'Could not open approval flow for editing.' });
  }

  router.replace({ query: restQuery }).catch(() => {});
};

const loadPanel = async () => {
  if (isHrisProfile.value || !jobProfile.value?.ulid) {
    return;
  }

  try {
    await loadPanelData(jobProfile.value.ulid);
    applyApprovalsDeepLink();
  } catch (error) {
    addErrorSnackbar({
      text: `Failed to load approvals — ${error.message}`,
    });

    throw error;
  }
};

const handleSubmitApprovalFlow = async () => {
  if (!jobProfile.value?.ulid) return;

  try {
    await createFlow({ draftUlid: jobProfile.value.ulid });

    useTracking().approvalPanelStartedFlow();

    useAppSnackbar().addSuccessSnackbar({
      text: 'Approval flow started',
    });
  } catch (error) {
    useAppSnackbar().addErrorSnackbar({
      text: `Failed to start approval flow — ${error.message}`,
    });

    throw error;
  }
};

const handleFlowClick = (flow) => {
  useTracking().approvalPanelViewedFlowDetail({ status: flow?.status || '' });

  openFlowDetail(flow.ulid);
};

onMounted(() => {
  loadPanel();
});

watch(
  () => [jobProfile.value?.ulid, isHrisProfile.value],
  () => {
    selectedTemplateUlid.value = null;
    viewOtherApprovals();
    loadPanel();
  },
);
</script>
